import { FolderOpen, Tag } from 'lucide-react';
import type { TemplateSaveMetadata } from './DataDiscoveryModal.saveMetadata';

export type TemplateSaveMetadataField = keyof TemplateSaveMetadata;

interface TemplateSaveMetadataFieldsProps {
  idPrefix: string;
  value: TemplateSaveMetadata;
  onFieldChange: (field: TemplateSaveMetadataField, value: string) => void;
  disabled?: boolean;
  nameRequired?: boolean;
  gridClassName?: string;
  inputClassName?: string;
  textareaClassName?: string;
  labelClassName?: string;
}

const defaultInputClassName =
  'w-full rounded-md border border-input bg-background px-2.5 py-1.5 text-sm text-foreground ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50';
const defaultTextareaClassName = `${defaultInputClassName} min-h-[72px] resize-y leading-5`;
const defaultLabelClassName = 'block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1';
const iconLabelClassName = 'text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 flex items-center gap-1';

export function TemplateSaveMetadataFields({
  idPrefix,
  value,
  onFieldChange,
  disabled = false,
  nameRequired = true,
  gridClassName = 'grid gap-3 sm:grid-cols-2',
  inputClassName = defaultInputClassName,
  textareaClassName = defaultTextareaClassName,
  labelClassName = defaultLabelClassName,
}: TemplateSaveMetadataFieldsProps) {
  const nameId = `${idPrefix}-name`;
  const descriptionId = `${idPrefix}-description`;
  const tagsId = `${idPrefix}-tags`;
  const folderId = `${idPrefix}-folder`;

  return (
    <div className={gridClassName}>
      <div>
        <div className="mb-1 flex items-center gap-1">
          <label htmlFor={nameId} className={`${labelClassName} mb-0`}>
            Name
          </label>
          {nameRequired && <span aria-hidden="true" className="text-red-500 dark:text-red-400">*</span>}
        </div>
        <input
          id={nameId}
          type="text"
          value={value.name}
          onChange={(event) => onFieldChange('name', event.target.value)}
          placeholder="Template name"
          disabled={disabled}
          aria-required={nameRequired}
          className={inputClassName}
        />
      </div>
      <div>
        <label htmlFor={descriptionId} className={labelClassName}>
          Description
        </label>
        <textarea
          id={descriptionId}
          value={value.description}
          onChange={(event) => onFieldChange('description', event.target.value)}
          placeholder="Optional description"
          disabled={disabled}
          rows={4}
          className={textareaClassName}
        />
      </div>
      <div>
        <label htmlFor={tagsId} className={iconLabelClassName}>
          <Tag className="h-3 w-3" />
          Tags
        </label>
        <textarea
          id={tagsId}
          value={value.tags}
          onChange={(event) => onFieldChange('tags', event.target.value)}
          placeholder="Comma-separated tags"
          disabled={disabled}
          rows={2}
          className={textareaClassName}
        />
      </div>
      <div>
        <label htmlFor={folderId} className={iconLabelClassName}>
          <FolderOpen className="h-3 w-3" />
          Folder
        </label>
        <input
          id={folderId}
          type="text"
          value={value.folder}
          onChange={(event) => onFieldChange('folder', event.target.value)}
          placeholder="e.g., Monitoring"
          disabled={disabled}
          className={inputClassName}
        />
      </div>
    </div>
  );
}
